import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import {
  Smartphone, Monitor, Laptop, Headphones, HandCoins, Repeat, Cpu, Download,
  ShieldCheck, MemoryStick, Battery, HardDrive, Zap, Layers, Wrench,
  Phone, MessageCircle, MapPin, Mail, Clock, ArrowRight, Menu, X, Microchip,
  Calendar, ClipboardList, Radio, Wifi, ScreenShare, BugOff, Monitor as WinIcon
} from "lucide-react";
import LabScene from "./components/LabScene";
import { BUSINESS, SALE, PURCHASE, REPAIRS, ALL_SERVICE_NAMES } from "./data/business";
import "./App.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ICONS = {
  "Mobile Phones": Smartphone,
  "Computers": Monitor,
  "Laptops": Laptop,
  "Accessories": Headphones,
  "Old Mobile Purchase": HandCoins,
  "Used Laptop Purchase": Laptop,
  "Computer Purchase": Monitor,
  "Device Exchange": Repeat,
  "Mobile Repair": Smartphone,
  "Computer Repair": Monitor,
  "Laptop Repair": Laptop,
  "Hardware Repair": Cpu,
  "Software Installation": Download,
  "Windows Installation": WinIcon,
  "Virus Removal": BugOff,
  "Motherboard Repair": Microchip,
  "Screen Replacement": ScreenShare,
  "Battery Replacement": Battery,
  "Data Recovery": HardDrive,
  "SSD Upgrade": Zap,
  "RAM Upgrade": MemoryStick,
};

function useReveal() {
  useEffect(() => {
    const els = document.querySelectorAll(".reveal");
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("visible");
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12 }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);
}

function buildWhatsAppLink(data) {
  const parts = [
    `*New Enquiry — ${BUSINESS.name}*`,
    `*Name:* ${data.name}`,
    `*Phone:* ${data.phone}`,
    `*Service:* ${data.service}`,
    `*Device:* ${data.device_type}`,
    `*Message:* ${data.message || "-"}`,
    `*Time:* ${new Date().toLocaleString()}`,
  ];
  return `https://wa.me/${BUSINESS.whatsapp}?text=${encodeURIComponent(parts.join("\n"))}`;
}

/* ---------- NAV ---------- */
function Nav() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  const links = [
    { href: "#home", label: "Home" },
    { href: "#sale", label: "Sale" },
    { href: "#purchase", label: "Purchase" },
    { href: "#repair", label: "Repair" },
    { href: "#contact", label: "Contact" },
  ];
  return (
    <header data-testid="site-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? "py-2" : "py-4"}`}>
      <div className="container-lab">
        <div className={`glass rounded-2xl px-4 md:px-6 py-3 flex items-center justify-between transition-all ${
          scrolled ? "shadow-[0_10px_40px_rgba(0,229,255,0.15)]" : ""}`}>
          <a href="#home" data-testid="nav-logo" className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg pulse-glow flex items-center justify-center"
              style={{ background: "linear-gradient(135deg, #00e5ff 0%, #2f80ff 100%)" }}>
              <Microchip size={20} color="#001217" strokeWidth={2.5} />
            </div>
            <div className="leading-tight">
              <div className="font-display font-bold text-[0.98rem] tracking-wide">Shri Bala Ji</div>
              <div className="font-mono-lab text-[0.62rem] uppercase tracking-[0.2em]" style={{ color: "var(--neon-cyan)" }}>
                Computer • Mobile • Lab
              </div>
            </div>
          </a>

          <nav className="hidden md:flex items-center gap-1">
            {links.map((l) => (
              <a key={l.href} href={l.href}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className="px-3 py-2 text-sm rounded-lg text-[color:var(--text-lo)] hover:text-white hover:bg-white/5 transition">
                {l.label}
              </a>
            ))}
            <a href={`tel:${BUSINESS.phoneIntl}`} data-testid="nav-call-btn" className="ml-3 neon-btn-ghost">
              <Phone size={14} /> Call
            </a>
          </nav>

          <button data-testid="nav-menu-toggle"
            className="md:hidden w-10 h-10 rounded-lg glass flex items-center justify-center"
            onClick={() => setOpen((v) => !v)} aria-label="Toggle menu">
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>

        {open && (
          <div className="md:hidden mt-2 glass rounded-2xl p-3 flex flex-col gap-1" data-testid="mobile-menu">
            {links.map((l) => (
              <a key={l.href} href={l.href} onClick={() => setOpen(false)}
                data-testid={`mobile-link-${l.label.toLowerCase()}`}
                className="px-3 py-2.5 rounded-lg hover:bg-white/5">{l.label}</a>
            ))}
            <a href={`tel:${BUSINESS.phoneIntl}`}
              className="px-3 py-2.5 rounded-lg text-[color:var(--neon-cyan)] flex items-center gap-2"
              data-testid="mobile-call">
              <Phone size={14} /> Call {BUSINESS.phone}
            </a>
          </div>
        )}
      </div>
    </header>
  );
}

/* ---------- HERO ---------- */
function Hero({ onBook }) {
  return (
    <section id="home" className="section pt-32 md:pt-36 relative overflow-hidden">
      <div className="container-lab grid lg:grid-cols-12 gap-10 items-center relative">
        <div className="lg:col-span-6 relative z-10">
          <span className="chip reveal" data-testid="hero-chip">
            <Wrench size={12} /> Repair Lab · Est. Moradabad
          </span>
          <h1 className="section-title mt-5 reveal" data-testid="hero-title">
            <span className="block">Professional</span>
            <span className="block"><span className="neon-text">Computer &amp; Mobile</span></span>
            <span className="block">Repair Experts</span>
          </h1>
          <p className="mt-6 text-[color:var(--text-lo)] text-lg leading-relaxed max-w-xl reveal" data-testid="hero-sub">
            Chip-level diagnostics, precision repairs, genuine parts. From mobiles &amp; laptops to full desktop builds — our lab treats every device like a mission-critical asset.
          </p>

          <div className="mt-8 flex flex-wrap gap-3 reveal">
            <button onClick={onBook} className="neon-btn" data-testid="hero-book-btn">
              <Calendar size={16} /> Book Repair
            </button>
            <a href={`https://wa.me/${BUSINESS.whatsapp}?text=Hi%20Shri%20Bala%20Ji%2C%20I%20need%20help%20with%20my%20device.`}
              target="_blank" rel="noreferrer" className="neon-btn-ghost" data-testid="hero-whatsapp-btn">
              <MessageCircle size={16} /> Chat on WhatsApp
            </a>
          </div>

          <div className="mt-10 grid grid-cols-3 gap-4 max-w-lg reveal" data-testid="hero-stats">
            {[{ k: "10k+", v: "Devices Repaired" }, { k: "98%", v: "Success Rate" }, { k: "24h", v: "Fast Turnaround" }].map((s) => (
              <div key={s.v} className="glass rounded-xl p-3">
                <div className="font-display text-xl md:text-2xl neon-text">{s.k}</div>
                <div className="text-[0.72rem] uppercase tracking-widest text-[color:var(--text-lo)]">{s.v}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="lg:col-span-6 relative h-[420px] md:h-[520px] lg:h-[600px]">
          <div className="absolute inset-0 rounded-3xl overflow-hidden">
            <LabScene className="absolute inset-0" />
            <div className="pointer-events-none absolute inset-0"
              style={{ background: "radial-gradient(60% 50% at 50% 50%, transparent 55%, rgba(5,7,10,0.85))" }} />
          </div>
          <div className="circuit-line" style={{ top: "20%", left: "-10%", width: "60%" }} />
          <div className="circuit-line" style={{ bottom: "18%", right: "-10%", width: "70%" }} />
        </div>
      </div>
    </section>
  );
}

/* ---------- Category Sections ---------- */
function CategorySection({ id, kicker, title, items, accent }) {
  return (
    <section id={id} className="section" data-testid={`section-${id}`}>
      <div className="container-lab">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10 reveal">
          <div>
            <span className="chip"><Radio size={12} /> {kicker}</span>
            <h2 className="section-title mt-4">
              {title.split(" ").map((w, i) => i === 1 ? (
                <span key={i} className="neon-text"> {w} </span>
              ) : (<span key={i}>{w} </span>))}
            </h2>
          </div>
          <div className="text-[color:var(--text-lo)] max-w-sm text-sm">
            Precision-engineered service by trained technicians using genuine tools &amp; parts.
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {items.map((it) => (<ServiceCard key={it.title} item={it} accent={accent} />))}
        </div>
      </div>
    </section>
  );
}

function ServiceCard({ item, accent }) {
  const Icon = ICONS[item.title] || Cpu;
  const scrollToForm = () => {
    document.getElementById("enquiry-form")?.scrollIntoView({ behavior: "smooth", block: "start" });
    window.dispatchEvent(new CustomEvent("prefill-service", { detail: item.title }));
  };
  return (
    <div className="service-card reveal" data-testid={`service-${item.title.replace(/\s+/g, "-").toLowerCase()}`}>
      <div className="flex items-start justify-between">
        <div className="svc-icon" style={accent ? { color: accent, borderColor: `${accent}66` } : {}}>
          <Icon size={22} />
        </div>
        <span className="font-mono-lab text-[0.62rem] tracking-widest text-[color:var(--text-lo)]">//LAB</span>
      </div>
      <h3 className="mt-5 font-display text-lg font-bold">{item.title}</h3>
      <p className="mt-2 text-sm text-[color:var(--text-lo)] leading-relaxed">{item.desc}</p>
      <button onClick={scrollToForm}
        className="mt-5 inline-flex items-center gap-2 text-sm font-semibold tracking-wide text-[color:var(--neon-cyan)] hover:text-white transition"
        data-testid={`service-cta-${item.title.replace(/\s+/g, "-").toLowerCase()}`}>
        Get Quote <ArrowRight size={14} />
      </button>
    </div>
  );
}

/* ---------- Marquee ---------- */
function BrandMarquee() {
  const items = ["Apple", "Samsung", "OnePlus", "Xiaomi", "Realme", "Vivo", "OPPO", "HP", "Dell", "Lenovo", "Asus", "Acer", "MSI", "Intel", "AMD", "NVIDIA"];
  return (
    <section className="py-10 relative overflow-hidden border-y" style={{ borderColor: "var(--lab-border)" }}>
      <div className="marquee-track flex gap-14 whitespace-nowrap">
        {[...items, ...items].map((b, i) => (
          <span key={i} className="font-display text-lg md:text-2xl tracking-widest text-[color:var(--text-lo)] opacity-80 hover:opacity-100">
            {b} <span className="text-[color:var(--neon-cyan)]">✦</span>
          </span>
        ))}
      </div>
    </section>
  );
}

/* ---------- CONTACT + FORM ---------- */
function Contact() {
  const [form, setForm] = useState({
    name: "", phone: "", service: ALL_SERVICE_NAMES[0], device_type: "Mobile", message: "",
  });
  const [status, setStatus] = useState({ state: "idle", msg: "" });
  const formRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      setForm((f) => ({ ...f, service: e.detail || f.service }));
      setTimeout(() => formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 120);
    };
    window.addEventListener("prefill-service", handler);
    return () => window.removeEventListener("prefill-service", handler);
  }, []);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) {
      setStatus({ state: "error", msg: "Please enter your name and phone number." });
      return;
    }
    setStatus({ state: "loading", msg: "Saving enquiry…" });
    try {
      await axios.post(`${API}/enquiries`, form, { timeout: 15000 });
    } catch (err) {
      setStatus({
        state: "error",
        msg: "Could not save your enquiry to our system. Please try again, or tap WhatsApp below to reach us directly.",
      });
      return;
    }
    const link = buildWhatsAppLink(form);
    window.open(link, "_blank", "noopener,noreferrer");
    setStatus({
      state: "success",
      msg: "Enquiry received. We've opened WhatsApp with your details — press Send to reach us instantly.",
    });
  };

  return (
    <section id="contact" className="section" data-testid="section-contact">
      <div className="container-lab">
        <div className="grid lg:grid-cols-12 gap-8">
          <div className="lg:col-span-5 reveal">
            <span className="chip"><Wifi size={12} /> Contact Lab</span>
            <h2 className="section-title mt-4">
              Book a <span className="neon-text">Diagnostic</span> — get a fast quote.
            </h2>
            <p className="mt-4 text-[color:var(--text-lo)] leading-relaxed">
              Visit our lab or reach out — we'll route your enquiry to the right technician in minutes.
            </p>

            <div className="mt-8 space-y-3">
              <InfoRow Icon={MapPin} title="Address"
                lines={[BUSINESS.address.line1, BUSINESS.address.line2, `${BUSINESS.address.city}, ${BUSINESS.address.state}`]}
                testid="info-address" />
              <InfoRow Icon={Phone} title="Phone" lines={[BUSINESS.phone]} link={`tel:${BUSINESS.phoneIntl}`} testid="info-phone" />
              <InfoRow Icon={MessageCircle} title="WhatsApp" lines={[BUSINESS.phone]}
                link={`https://wa.me/${BUSINESS.whatsapp}`} testid="info-whatsapp" />
              <InfoRow Icon={Mail} title="Email" lines={[BUSINESS.email]} link={`mailto:${BUSINESS.email}`} testid="info-email" />
              <InfoRow Icon={Clock} title="Working Hours" lines={[BUSINESS.hours]} testid="info-hours" />
            </div>

            <div className="mt-8 flex flex-wrap gap-3">
              <a href={`tel:${BUSINESS.phoneIntl}`} className="neon-btn" data-testid="contact-call-btn">
                <Phone size={16} /> Call Now
              </a>
              <a href={`https://wa.me/${BUSINESS.whatsapp}`} target="_blank" rel="noreferrer" className="neon-btn-ghost" data-testid="contact-whatsapp-btn">
                <MessageCircle size={16} /> WhatsApp
              </a>
              <a href={`https://www.google.com/maps/search/?api=1&query=${BUSINESS.mapsQuery}`}
                target="_blank" rel="noreferrer" className="neon-btn-ghost" data-testid="contact-location-btn">
                <MapPin size={16} /> Location
              </a>
            </div>

            <div className="mt-8 glass rounded-2xl overflow-hidden h-64" data-testid="map-embed">
              <iframe title="Map"
                src={`https://www.google.com/maps?q=${BUSINESS.mapsQuery}&output=embed`}
                width="100%" height="100%"
                style={{ border: 0, filter: "invert(0.92) hue-rotate(180deg) saturate(0.7) brightness(0.85)" }}
                loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
            </div>
          </div>

          <div className="lg:col-span-7 reveal">
            <form id="enquiry-form" ref={formRef} onSubmit={onSubmit}
              data-testid="enquiry-form" className="glass rounded-3xl p-6 md:p-8 relative">
              <div className="absolute -top-3 left-6 chip bg-[#05070a]">
                <ClipboardList size={12} /> Enquiry Form
              </div>
              <div className="grid md:grid-cols-2 gap-4 mt-4">
                <div>
                  <label className="text-xs uppercase tracking-widest text-[color:var(--text-lo)]">Name</label>
                  <input value={form.name} onChange={onChange("name")} required
                    data-testid="form-input-name" className="lab-input mt-1" placeholder="Your full name" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-[color:var(--text-lo)]">Phone</label>
                  <input value={form.phone} onChange={onChange("phone")} required
                    data-testid="form-input-phone" className="lab-input mt-1" placeholder="10-digit mobile number" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-[color:var(--text-lo)]">Service Required</label>
                  <select value={form.service} onChange={onChange("service")}
                    data-testid="form-input-service" className="lab-input mt-1">
                    {ALL_SERVICE_NAMES.map((s) => (<option key={s} value={s}>{s}</option>))}
                  </select>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-[color:var(--text-lo)]">Device Type</label>
                  <select value={form.device_type} onChange={onChange("device_type")}
                    data-testid="form-input-device" className="lab-input mt-1">
                    {["Mobile", "Laptop", "Desktop", "Tablet", "Accessory", "Other"].map((d) => (<option key={d}>{d}</option>))}
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="text-xs uppercase tracking-widest text-[color:var(--text-lo)]">Message</label>
                  <textarea value={form.message} onChange={onChange("message")} rows={4}
                    placeholder="Describe the issue or what you need…"
                    data-testid="form-input-message" className="lab-input mt-1 resize-none" />
                </div>
              </div>

              <div className="mt-5 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <p className="text-xs text-[color:var(--text-lo)] leading-relaxed">
                  Submitting saves your enquiry in our system and instantly opens WhatsApp with your details pre-filled for the fastest response.
                </p>
                <button type="submit" className="neon-btn" data-testid="form-submit-btn"
                  disabled={status.state === "loading"}>
                  <MessageCircle size={16} />
                  {status.state === "loading" ? "Sending…" : "Submit & Send to WhatsApp"}
                </button>
              </div>

              {status.state !== "idle" && (
                <div className={`mt-5 rounded-xl px-4 py-3 text-sm border ${
                  status.state === "error" ? "border-red-500/40 text-red-300 bg-red-500/10"
                    : status.state === "success" ? "border-emerald-400/40 text-emerald-200 bg-emerald-400/10"
                      : "border-cyan-400/30 text-cyan-100 bg-cyan-400/10"
                }`} data-testid="form-status">
                  {status.msg}
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}

function InfoRow({ Icon, title, lines, link, testid }) {
  const Body = (
    <div className="flex items-start gap-4 glass rounded-xl p-4 hover:border-[color:var(--neon-cyan)]/40 transition">
      <div className="svc-icon shrink-0"><Icon size={20} /></div>
      <div>
        <div className="text-xs uppercase tracking-widest text-[color:var(--text-lo)]">{title}</div>
        {lines.map((l, i) => (<div key={i} className="text-[0.98rem]">{l}</div>))}
      </div>
    </div>
  );
  return link ? (
    <a href={link} target={link.startsWith("http") ? "_blank" : undefined} rel="noreferrer" data-testid={testid}>{Body}</a>
  ) : (<div data-testid={testid}>{Body}</div>);
}

/* ---------- FOOTER ---------- */
function Footer() {
  return (
    <footer className="pt-16 pb-10 border-t" style={{ borderColor: "var(--lab-border)" }} data-testid="site-footer">
      <div className="container-lab grid md:grid-cols-4 gap-8">
        <div className="md:col-span-2">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg pulse-glow flex items-center justify-center"
              style={{ background: "linear-gradient(135deg, #00e5ff 0%, #2f80ff 100%)" }}>
              <Microchip size={20} color="#001217" strokeWidth={2.5} />
            </div>
            <div>
              <div className="font-display font-bold">{BUSINESS.name}</div>
              <div className="font-mono-lab text-[0.68rem] uppercase tracking-widest text-[color:var(--neon-cyan)]">
                Premium Repair Lab
              </div>
            </div>
          </div>
          <p className="mt-4 text-[color:var(--text-lo)] max-w-md text-sm leading-relaxed">
            Your trusted destination in Aghwanpur for mobile, laptop &amp; computer sale, purchase and expert repair.
          </p>
        </div>
        <div>
          <h4 className="font-display text-sm uppercase tracking-widest text-[color:var(--neon-cyan)]">Visit</h4>
          <p className="mt-3 text-sm text-[color:var(--text-lo)]">
            {BUSINESS.address.line1}<br />
            {BUSINESS.address.line2}<br />
            {BUSINESS.address.city}, {BUSINESS.address.state}
          </p>
        </div>
        <div>
          <h4 className="font-display text-sm uppercase tracking-widest text-[color:var(--neon-cyan)]">Reach</h4>
          <ul className="mt-3 space-y-2 text-sm">
            <li><a className="hover:text-white text-[color:var(--text-lo)] flex items-center gap-2" href={`tel:${BUSINESS.phoneIntl}`}><Phone size={14} />{BUSINESS.phone}</a></li>
            <li><a className="hover:text-white text-[color:var(--text-lo)] flex items-center gap-2" href={`https://wa.me/${BUSINESS.whatsapp}`} target="_blank" rel="noreferrer"><MessageCircle size={14} />WhatsApp</a></li>
            <li><a className="hover:text-white text-[color:var(--text-lo)] flex items-center gap-2" href={`mailto:${BUSINESS.email}`}><Mail size={14} />{BUSINESS.email}</a></li>
            <li className="text-[color:var(--text-lo)] flex items-center gap-2"><Clock size={14} />{BUSINESS.hours}</li>
          </ul>
        </div>
      </div>
      <div className="divider-line my-8"></div>
      <div className="container-lab flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-[color:var(--text-lo)]">
        <div>© {new Date().getFullYear()} {BUSINESS.name}. All rights reserved.</div>
        <div className="font-mono-lab tracking-widest">CRAFTED FOR PRECISION · REPAIR LAB v1.0</div>
      </div>
    </footer>
  );
}

/* ---------- APP ---------- */
export default function App() {
  useReveal();
  const scrollToForm = () => {
    document.getElementById("enquiry-form")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="App relative">
      <Nav />
      <Hero onBook={scrollToForm} />
      <BrandMarquee />
      <CategorySection id="sale" kicker="Category · 01" title="We Sale Devices" items={SALE} accent="#00e5ff" />
      <CategorySection id="purchase" kicker="Category · 02" title="We Purchase Old" items={PURCHASE} accent="#22e6a3" />
      <CategorySection id="repair" kicker="Category · 03" title="We Repair Everything" items={REPAIRS} accent="#2f80ff" />
      <Contact />
      <Footer />
      <a href={`https://wa.me/${BUSINESS.whatsapp}`} target="_blank" rel="noreferrer"
        className="fixed bottom-5 right-5 z-50 w-14 h-14 rounded-full flex items-center justify-center pulse-glow"
        style={{ background: "linear-gradient(135deg, #22e6a3 0%, #00c37d 100%)", color: "#00170e" }}
        data-testid="floating-whatsapp" aria-label="Chat on WhatsApp">
        <MessageCircle size={28} strokeWidth={2.5} />
      </a>
    </div>
  );
}
